import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client.ts";
import type { Brand, Domain } from "../api/types.ts";
import { useAuth } from "../auth/AuthProvider.tsx";
import { BrandSwitcher } from "./BrandSwitcher.tsx";
import { FeedbackHost } from "./feedback.tsx";
import {
  BrandIcon,
  CampaignIcon,
  ContactsIcon,
  DashboardIcon,
  DomainIcon,
  ListIcon,
  MediaIcon,
  PulseIcon,
  ReplyIcon,
  SendIcon,
  SequenceIcon,
  ServerIcon,
  TemplateIcon,
  UsersIcon,
} from "./icons.tsx";
import { UserMenu } from "./UserMenu.tsx";
import { guardLinks } from "./unsaved.ts";

export function AppShell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Below 900px the sidebar is a slide-out menu; navigating closes it.
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on every route change.
  useEffect(() => setMenuOpen(false), [location.pathname]);
  const navigate = useNavigate();
  useEffect(() => guardLinks(navigate), [navigate]);

  /**
   * Config is admin-only, so a member gets a 401/403 here. That is expected
   * rather than an error: the switcher simply has nothing to offer them yet.
   */
  const domains = useQuery({
    queryKey: ["domains"],
    queryFn: () => api.get<Domain[]>("/config/domains"),
    retry: false,
  });
  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
    retry: false,
  });
  // Under the ["replies"] prefix, so marking a reply handled refreshes it.
  const unhandled = useQuery({
    queryKey: ["replies", "count"],
    queryFn: () => api.get<{ count: number }>("/inbox/unhandled-count"),
    refetchInterval: 30_000,
  });
  const unhandledCount = unhandled.data?.count ?? 0;

  return (
    <div className={`shell${menuOpen ? " menu-open" : ""}`}>
      <button
        type="button"
        className="sidebar-scrim"
        aria-label="Close menu"
        tabIndex={-1}
        onClick={() => setMenuOpen(false)}
      />
      <aside className="sidebar" id="sidebar">
        <div className="brand-row">
          <div className="brand-mark">S</div>
          <div className="brand-name">Send</div>
        </div>

        <nav className="nav">
          <NavLink to="/" end className="nav-item">
            <DashboardIcon />
            Dashboard
          </NavLink>

          <div className="nav-section">Send</div>
          <NavLink to="/campaigns" className="nav-item">
            <CampaignIcon />
            Campaigns
          </NavLink>
          <NavLink to="/sequences" className="nav-item">
            <SequenceIcon />
            Sequences
          </NavLink>
          <NavLink to="/templates" className="nav-item">
            <TemplateIcon />
            Templates
          </NavLink>
          <NavLink to="/media" className="nav-item">
            <MediaIcon />
            Media
          </NavLink>
          <NavLink to="/replies" className="nav-item">
            <ReplyIcon />
            Replies
            {unhandledCount > 0 && (
              <span className="count alert" title={`${unhandledCount} to handle`}>
                {unhandledCount > 99 ? "99+" : unhandledCount}
              </span>
            )}
          </NavLink>
          <NavLink to="/test-send" className="nav-item">
            <SendIcon />
            Test send
          </NavLink>

          <div className="nav-section">Audience</div>
          <NavLink to="/contacts" className="nav-item">
            <ContactsIcon />
            Contacts
          </NavLink>
          <NavLink to="/imports" className="nav-item">
            <ListIcon />
            Lists &amp; imports
          </NavLink>

          <div className="nav-section">Setup</div>
          <NavLink to="/settings/brands" className="nav-item">
            <BrandIcon />
            Brands
          </NavLink>
          <NavLink to="/settings/postal" className="nav-item">
            <ServerIcon />
            Postal
          </NavLink>
          <NavLink to="/settings/pulse" className="nav-item">
            <PulseIcon />
            Pulse
          </NavLink>
          <NavLink to="/settings/domains" className="nav-item">
            <DomainIcon />
            Domains &amp; senders
            {domains.data && <span className="count">{domains.data.length}</span>}
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/settings/team" className="nav-item">
              <UsersIcon />
              Team
            </NavLink>
          )}
        </nav>

        <div className="sidebar-footer">
          <UserMenu />
        </div>
      </aside>

      <div className="main">
        <WorkerBanner />
        <div className="top-bar">
          <button
            type="button"
            className="menu-toggle"
            aria-label="Menu"
            aria-controls="sidebar"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            ☰
          </button>
          <BrandSwitcher brands={brands.data ?? []} domains={domains.data ?? []} />
        </div>
        {children}
      </div>
      <FeedbackHost />
    </div>
  );
}

/**
 * Imports, sequences and broadcasts all happen in the worker, and when it is
 * down none of them fail — they just wait. Without this an upload sits on
 * "validating" forever with no explanation anywhere.
 */
function WorkerBanner() {
  const status = useQuery({
    queryKey: ["status"],
    queryFn: () =>
      api.get<{ worker: { online: boolean; lastTickAt: string | null; lastError: string | null } }>(
        "/status",
      ),
    refetchInterval: 30_000,
  });

  const worker = status.data?.worker;
  if (!worker) return null;

  if (!worker.online) {
    return (
      <div className="banner bad" role="alert">
        <strong>The background worker isn&rsquo;t running.</strong> Imports won&rsquo;t validate,
        and sequences and broadcasts won&rsquo;t send, until it is.{" "}
        {worker.lastTickAt
          ? `Last seen ${new Date(worker.lastTickAt).toLocaleString()}.`
          : "It has never run against this database."}{" "}
        Start it with <code>bun dev:worker</code>.
      </div>
    );
  }

  // Running, but the last tick hit a problem worth knowing about.
  if (worker.lastError) {
    return (
      <div className="banner warn" role="status">
        <strong>The worker hit an error on its last run:</strong> {worker.lastError}
      </div>
    );
  }
  return null;
}
